// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyActiveTheme, TyApiResponse, TyMenuThemeOption } from "@/types";
import { getApiAssetUrl } from "@/utils";
import MenuThemePopup from "@/UI/popups/MenuThemePopup";
import SettingsCard from "@/UI/components/SettingsCard";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

/**
 * The theme gallery. Every theme the menu can render, read off the backend rather than listed here —
 * shipping a theme is adding a folder, and this grid grows on its own with nothing to edit.
 *
 * Two ways out of a tile, because they answer different questions. "Choose" is for an owner who likes
 * what they see and wants it now; it applies the theme with everything that theme offers, which is a
 * safe default because every option stays switchable by the customer. "Customize" is for an owner who
 * wants to narrow that down first, and opens the popup where nothing is live until they apply.
 */
export default function SettingsMenuTheme() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { canEdit } = usePermission();
    const addComp = usePopup((state) => state.addComp);

    const editable = canEdit("settings");

    const { data: themesData, loading: themesLoading } = useFetch<TyApiResponse<{ themes: TyMenuThemeOption[] }>>("/settings/menu-themes");
    const { data: activeData } = useFetch<TyApiResponse<{ activeTheme: TyActiveTheme }>>("/settings/active-theme");
    const {
        data: chooseData,
        loading: choosing,
        error: chooseError,
        invoke: choose,
    } = useFetch<TyApiResponse<{ activeTheme: TyActiveTheme }>>("/settings/active-theme", { method: "PUT" }, false);

    // The card's own copy: seeded by the fetch, then replaced by whatever a save returns, so the badge
    // moves to the newly applied theme without re-fetching the whole gallery behind it.
    const [active, setActive] = useState<TyActiveTheme | null>(null);
    const [applied, setApplied] = useState(false);
    /** Which tile's "choose" is in flight — only that one shows a spinner, not all of them. */
    const [choosingKey, setChoosingKey] = useState<string | null>(null);

    useEffect(() => {
        const fetched = activeData?.data?.activeTheme;
        if (fetched) setActive(fetched);
    }, [activeData]);

    useEffect(() => {
        if (!chooseData?.success) return;
        setActive(chooseData.data.activeTheme);
        setChoosingKey(null);
        setApplied(true);
    }, [chooseData]);

    useEffect(() => {
        if (chooseError) setChoosingKey(null);
    }, [chooseError]);

    useEffect(() => {
        if (!applied) return;
        const timer = setTimeout(() => setApplied(false), 2600);
        return () => clearTimeout(timer);
    }, [applied]);

    const themes = themesData?.data?.themes ?? [];

    // --- Handlers --------------------------------------------------------------------------------

    const handleSuccess = (activeTheme: TyActiveTheme) => {
        setActive(activeTheme);
        setApplied(true);
    };

    /** Straight to the menu, with every option the theme offers left switchable for customers. */
    const handleChoose = (theme: TyMenuThemeOption) => {
        if (choosing) return;
        setChoosingKey(theme.key);
        choose({ method: "PUT", data: { theme: theme.key, color: active?.color ?? null } });
    };

    const handleCustomize = (theme: TyMenuThemeOption) => {
        if (!active) return;
        addComp({
            name: "menu-theme",
            Component: MenuThemePopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: { theme, active, onSuccess: handleSuccess },
        });
    };

    return (
        <SettingsCard
            icon="/icons/store-icon.svg"
            title={t.menuTheme.title}
            subtitle={t.menuTheme.subtitle}
            badge={editable ? undefined : t.menuTheme.readOnlyBadge}
        >
            {applied && <p className="smt-applied">{t.menuTheme.appliedMessage}</p>}
            {chooseError && <p className="smt-error">{chooseError}</p>}

            {themesLoading && <p className="smt-note">{t.menuTheme.loadingThemes}</p>}
            {!themesLoading && themes.length === 0 && <p className="smt-note">{t.menuTheme.noThemes}</p>}

            <div className="smt-grid">
                {themes.map((theme) => {
                    const isActive = active?.theme === theme.key;
                    const name = lang === "ar" ? theme.name.ar || theme.name.en : theme.name.en || theme.name.ar;
                    const description = lang === "ar" ? theme.description.ar || theme.description.en : theme.description.en || theme.description.ar;

                    return (
                        <article key={theme.key} className="smt-theme" data-active={isActive}>
                            <div className="smt-theme-top">
                                <div className="smt-theme-shot">
                                    {/* A theme ships before its picture is taken, so a missing file is a
                                        normal state rather than a broken image — the tile falls back to
                                        the placeholder and the popup's live preview does the selling. */}
                                    <img
                                        src={getApiAssetUrl(theme.preview)}
                                        alt={name}
                                        loading="lazy"
                                        onError={(e) => {
                                            (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
                                        }}
                                    />
                                </div>

                                <div className="smt-theme-text">
                                    <div className="smt-theme-title">
                                        <p className="smt-theme-name">{name}</p>
                                        {isActive && <span className="smt-theme-badge">{t.menuTheme.activeBadge}</span>}
                                    </div>
                                    <p className="smt-theme-desc">{description}</p>
                                </div>
                            </div>

                            {editable && (
                                <div className="smt-theme-actions">
                                    <button
                                        type="button"
                                        className="smt-theme-btn smt-theme-btn--choose"
                                        onClick={() => handleChoose(theme)}
                                        disabled={!active || isActive || choosing}
                                    >
                                        {choosingKey === theme.key
                                            ? t.menuTheme.applyingLabel
                                            : isActive
                                              ? t.menuTheme.currentLabel
                                              : t.menuTheme.chooseLabel}
                                    </button>
                                    <button
                                        type="button"
                                        className="smt-theme-btn smt-theme-btn--customize"
                                        onClick={() => handleCustomize(theme)}
                                        disabled={!active || choosing}
                                    >
                                        {t.menuTheme.customizeLabel}
                                    </button>
                                </div>
                            )}
                        </article>
                    );
                })}
            </div>
        </SettingsCard>
    );
}
